import { createFileRoute, Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Suspense, lazy, useMemo, useState, type ComponentType } from "react";
import {
  LayoutDashboard,
  Users,
  FileText,
  ShieldCheck,
  LogOut,
  Menu,
  Loader2,
  UserCircle2,
  Settings,
  Star,
  ChevronDown,
  History,
  CalendarClock,
  Trophy,
  Megaphone,
  ChevronLeft,
  Inbox,
  MapPinned,
  Gauge,
  UserCog,
  X,
} from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useAuth } from "@/hooks/use-auth";
import { crmGetMyAccess } from "@/lib/crm.functions";
import { getPartnerApplicationCounts } from "@/lib/partner-signups.functions";
import { supabase } from "@/integrations/supabase/client";
import adminCss from "../styles.admin.css?url";
import { DevViewportIndicator } from "@/components/DevViewportIndicator";
import { HeaderChatLink, HeaderMyWorkLink } from "@/components/crm/HeaderChatLink";
import { SMM_ROUTE_PATHS, validateSmmHref } from "@/lib/smm-routes";
import { WorkspaceHeader, WorkspacePageTitle, WorkspaceWordmark } from "@/components/WorkspaceHeader";

const CrmRouteDebugPanel = lazy(() =>
  import("@/components/crm/CrmRouteDebugPanel").then((m) => ({ default: m.CrmRouteDebugPanel })),
);

export const Route = createFileRoute("/crm")({
  // CRM-only Tailwind utilities ship in their own stylesheet, keeping them out
  // of the public site's render-blocking CSS.
  head: () => ({ links: [{ rel: "stylesheet", href: adminCss }] }),
  component: CRMShell,
});

const BS = {
  primary: "#0d6efd",
  bodyBg: "#f8f9fa",
  cardBorder: "#dee2e6",
  muted: "#6c757d",
  dark: "#212529",
};

const btnOutline =
  "inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border transition-colors disabled:opacity-60";

type NavKey =
  | "tasks"
  | "invoices"
  | "dashboard"
  | "smm"
  | "clients"
  | "map"
  | "mylist"
  | "logs"
  | "reports"
  | "closed-deals"
  | "sales-people"
  | "access"
  | "admin-panel"
  | "applications"
  | "diag-locations";

type NavEntry = {
  key: NavKey;
  label: string;
  navLabel: string;
  description: string;
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
  to: string;
  search?: Record<string, any>;
  matchPrefix?: string;
  adminOnly?: boolean;
  salesRepOnly?: boolean;
};

const NAV_GROUPS: { title: string; items: NavEntry[] }[] = [
  {
    title: "Workspace",
    items: [
      {
        key: "dashboard",
        label: "Dashboard",
        navLabel: "Dashboard",
        description: "Pipeline, outreach and team activity.",
        icon: LayoutDashboard,
        to: "/crm/dashboard",
      },
      {
        key: "smm",
        label: "Social Media",
        navLabel: "SMM",
        description: "Social media operations workspace.",
        icon: Megaphone,
        to: SMM_ROUTE_PATHS.dashboard,
      },
    ],
  },
  {
    title: "Pipeline",
    items: [
      {
        key: "clients",
        label: "Clients",
        navLabel: "Clients",
        description: "Prospects, contracts and active accounts.",
        icon: Users,
        to: "/crm",
        matchPrefix: "/crm/$clientId",
      },
      {
        key: "map",
        label: "Territory Map",
        navLabel: "Map",
        description: "Clients and locations on the map.",
        icon: MapPinned,
        to: "/crm/map",
      },
      {
        key: "mylist",
        label: "My Clients",
        navLabel: "My List",
        description: "Clients you added.",
        icon: Star,
        to: "/crm",
        search: { mine: 1 },
        salesRepOnly: true,
      },
      {
        key: "applications",
        label: "Partner Applications",
        navLabel: "Apps",
        description: "Incoming partner applications.",
        icon: Inbox,
        to: "/crm/applications",
      },
      {
        key: "tasks",
        label: "Tasks & Reminders",
        navLabel: "Tasks",
        description: "Calls, meetings and follow-ups.",
        icon: CalendarClock,
        to: "/crm/tasks",
      },
      {
        key: "invoices",
        label: "Invoice Tracking",
        navLabel: "Invoices",
        description: "Who is invoiced, unpaid or paid.",
        icon: FileText,
        to: "/crm/invoices",
      },
      {
        key: "logs",
        label: "Activity Log",
        navLabel: "Log",
        description: "Recent CRM activity.",
        icon: History,
        to: "/crm/logs",
      },
    ],
  },
  {
    title: "Admin",
    items: [
      {
        key: "reports",
        label: "Reports",
        navLabel: "Reports",
        description: "Pipeline and contact reports.",
        icon: FileText,
        to: "/crm/reports",
        adminOnly: true,
      },
      {
        key: "closed-deals",
        label: "Closed Deals",
        navLabel: "Deals",
        description: "Won deals and closed revenue.",
        icon: Trophy,
        to: "/crm/closed-deals",
        adminOnly: true,
      },
      {
        key: "sales-people",
        label: "Sales Team",
        navLabel: "Team",
        description: "Salespeople and their pipelines.",
        icon: UserCog,
        to: "/crm/sales",
        matchPrefix: "/crm/sales",
        adminOnly: true,
      },
      {
        key: "access",
        label: "Access Control",
        navLabel: "Access",
        description: "Who can use the CRM.",
        icon: ShieldCheck,
        to: "/crm/access",
        adminOnly: true,
      },
      {
        key: "diag-locations",
        label: "Location Capacity",
        navLabel: "Capacity",
        description: "City location capacity diagnostics.",
        icon: Gauge,
        to: "/crm/diagnostics/locations",
        adminOnly: true,
      },
    ],
  },
];

const CLIENT_EXCLUDES = [
  "/crm/dashboard",
  "/crm/smm",
  "/crm/reports",
  "/crm/access",
  "/crm/closed-deals",
  "/crm/sales",
  "/crm/logs",
  "/crm/profile",
  "/crm/applications",
  "/crm/tasks",
  "/crm/invoices",
  "/crm/map",
  "/crm/diagnostics",
  "/crm/chat",
  "/crm/my-work",
  "/crm/settings",
];

function CRMShell() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const getAccess = useServerFn(crmGetMyAccess);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const locSearch = useRouterState({ select: (s) => s.location.search as Record<string, unknown> });
  const isMineRoute = pathname === "/crm" && (locSearch?.mine === 1 || locSearch?.mine === "1");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const smmMenuValidations = useMemo(
    () =>
      [
        ...NAV_GROUPS.flatMap((group) =>
          group.items
            .filter((item) => item.to.startsWith("/crm/smm"))
            .map((item) => ({ ...validateSmmHref(item.to), label: item.label })),
        ),
        { ...validateSmmHref(SMM_ROUTE_PATHS.members), label: "SMM Members" },
        { ...validateSmmHref(SMM_ROUTE_PATHS.reports), label: "SMM Reports" },
      ],
    [],
  );
  const validatedNavGroups = useMemo(
    () =>
      NAV_GROUPS.map((group) => ({
        ...group,
        items: group.items.map((item) => {
          if (!item.to.startsWith("/crm/smm")) return item;
          const validation = validateSmmHref(item.to);
          return validation.output === item.to ? item : { ...item, to: validation.output };
        }),
      })),
    [],
  );
  const forceRouteDebug =
    locSearch?.routeDebug === 1 ||
    locSearch?.routeDebug === "1" ||
    locSearch?.routeRecovery === 1 ||
    locSearch?.routeRecovery === "1";

  const accessQ = useQuery({
    queryKey: ["crm", "me"],
    queryFn: () => getAccess(),
    enabled: !!user,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const countsFn = useServerFn(getPartnerApplicationCounts);
  const applicationCountsQ = useQuery({
    queryKey: ["admin", "partner-application-counts"],
    queryFn: () => countsFn(),
    enabled: !!user && accessQ.data?.is_admin === true,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
  const waitingApplications = applicationCountsQ.data?.submitted ?? 0;

  if (!loading && !user) {
    return <CRMLoginPage />;
  }

  if (loading || (user && accessQ.isLoading)) {
    return <CRMShellSkeleton />;
  }

  if (user && !accessQ.data?.has_crm_access) {
    return (
      <div className="grid min-h-screen place-items-center px-6 text-center" style={{ background: BS.bodyBg }}>
        <div className="max-w-md space-y-3">
          <h1 className="text-xl font-semibold" style={{ color: BS.dark }}>
            CRM Access Not Enabled
          </h1>
          <p className="text-sm" style={{ color: BS.muted }}>
            This account doesn't have CRM access. Ask the super admin to add you as a CRM user.
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => navigate({ to: "/" })}
              className={btnOutline}
              style={{ borderColor: BS.cardBorder, color: BS.dark }}
            >
              Back To Site
            </button>
            <button
              onClick={async () => {
                await supabase.auth.signOut();
                navigate({ to: "/admin/login", replace: true });
              }}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded text-white"
              style={{ background: BS.primary }}
            >
              Sign Out & Use Another Account
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isAdmin = accessQ.data?.is_admin === true;

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate({ to: "/admin/login", replace: true });
  };

  const isItemActive = (item: NavEntry) => {
    if (item.key === "mylist") return isMineRoute;
    if (item.key === "clients") {
      if (isMineRoute) return false;
      return (
        pathname === "/crm" ||
        (pathname.startsWith("/crm/") && !CLIENT_EXCLUDES.some((p) => pathname.startsWith(p)))
      );
    }
    if (item.matchPrefix) return pathname.startsWith(item.matchPrefix);
    return pathname === item.to || pathname.startsWith(`${item.to}/`);
  };

  const allNav = validatedNavGroups.flatMap((g) => g.items);
  const pageMeta = isMineRoute
    ? (allNav.find((i) => i.key === "mylist") ?? null)
    : pathname === "/crm" ||
        (pathname.startsWith("/crm/") && !CLIENT_EXCLUDES.some((p) => pathname.startsWith(p)))
      ? (allNav.find((i) => i.key === "clients") ?? null)
      : (allNav.find((i) => isItemActive(i) && i.key !== "clients" && i.key !== "mylist") ?? null);

  const hidePageHeader =
    pathname.startsWith("/crm/chat") ||
    pathname.startsWith("/crm/smm") ||
    (pathname.startsWith("/crm/") &&
      !CLIENT_EXCLUDES.some((p) => pathname.startsWith(p)) &&
      pathname !== "/crm");

  if (pathname.startsWith("/crm/chat")) {
    return (
      <div className="min-h-screen" style={{ background: BS.bodyBg, fontFamily: "'Poppins', system-ui, sans-serif" }}>
        <Outlet />
      </div>
    );
  }

  if (pathname.startsWith("/crm/smm")) {
    return (
      <div
        className="h-dvh overflow-hidden flex flex-col"
        style={{ background: BS.bodyBg, fontFamily: "'Poppins', system-ui, sans-serif" }}
      >
        <WorkspaceHeader
          current="smm"
          end={
            <div className="flex items-center gap-1">
              <HeaderMyWorkLink variant="chrome" />
              <HeaderChatLink variant="chrome" />
            </div>
          }
        />
        <main className="flex-1 min-w-0 min-h-0 w-full overflow-hidden flex flex-col">
          <Outlet />
        </main>
        {forceRouteDebug ? (
          <Suspense fallback={null}>
            <CrmRouteDebugPanel navLinks={smmMenuValidations} forceOpen />
          </Suspense>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className="h-dvh overflow-hidden flex flex-col"
      style={{ background: "#f4f6f8", fontFamily: "'Poppins', system-ui, sans-serif" }}
    >
      <WorkspaceHeader
        current="crm"
        badge={{ label: isAdmin ? "Admin" : "Rep", tone: isAdmin ? "gold" : "blue" }}
        onMenu={() => setSidebarOpen((v) => !v)}
        end={
          <div className="flex items-center gap-1">
            <HeaderMyWorkLink variant="chrome" />
            <HeaderChatLink variant="chrome" />
          </div>
        }
      />

      <div className="flex-1 flex relative min-h-0">
        {sidebarOpen ? (
          <button
            type="button"
            aria-label="Close navigation"
            className="fixed inset-0 z-40 bg-black/40 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        ) : null}

        <aside
          className={`${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          } md:translate-x-0 fixed md:static top-[53px] md:top-auto bottom-0 left-0 z-40 w-[232px] min-h-0 overflow-y-auto overscroll-contain [scrollbar-width:thin] border-r bg-[#f8f9fb] transition-transform duration-150 ease-out shadow-xl md:shadow-none`}
          style={{ borderColor: "#eceff3" }}
        >
          <div className="mb-1 flex items-center justify-between px-3 pt-3 md:hidden">
            <span className="text-sm font-semibold text-[#212529]">Menu</span>
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close"
              className="rounded-lg p-1.5 hover:bg-[#eef1f5]"
            >
              <X className="size-4" />
            </button>
          </div>
          <div className="px-2.5 py-3 pb-4">
            {validatedNavGroups.map((group) => {
              const items = group.items.filter(
                (it) => (!it.adminOnly || isAdmin) && (!it.salesRepOnly || !isAdmin),
              );
              if (items.length === 0) return null;
              return (
                <div key={group.title} className="mb-3">
                  <div className="px-2.5 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8b95a1]">
                    {group.title}
                  </div>
                  <nav className="space-y-0.5">
                    {items.map((item) => {
                      const Icon = item.icon;
                      const active = isItemActive(item);
                      const showBadge = item.key === "applications" && waitingApplications > 0;
                      return (
                        <Link
                          key={item.key}
                          to={item.to}
                          search={item.search as any}
                          onClick={() => setSidebarOpen(false)}
                          className="w-full flex items-center gap-2 px-1.5 py-[6px] rounded-lg text-[13px] font-medium text-left transition-colors duration-150"
                          style={{
                            background: active ? BS.primary : "transparent",
                            color: active ? "white" : "#1f2937",
                          }}
                          onMouseEnter={(e) => {
                            if (!active) e.currentTarget.style.background = "#eef1f5";
                          }}
                          onMouseLeave={(e) => {
                            if (!active) e.currentTarget.style.background = "transparent";
                          }}
                        >
                          <span
                            className="size-6 rounded-md flex items-center justify-center shrink-0"
                            style={{
                              background: active ? "rgba(255,255,255,0.18)" : "#eceff3",
                              color: active ? "white" : "#4b5563",
                            }}
                          >
                            <Icon className="size-3.5" strokeWidth={1.85} />
                          </span>
                          <span className="flex-1 truncate leading-none">{item.navLabel}</span>
                          {showBadge && (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center bg-[#dc3545] text-white">
                              {waitingApplications > 99 ? "99+" : waitingApplications}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </nav>
                </div>
              );
            })}

            <div className="mt-2 pt-3 border-t" style={{ borderColor: "#eceff3" }}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="w-full flex items-center gap-2 px-1.5 py-[6px] rounded-lg text-[13px] font-medium text-left hover:bg-[#eef1f5]"
                    style={{ color: "#1f2937" }}
                  >
                    <span
                      className="grid place-items-center size-6 rounded-md text-white font-bold text-[10px] shrink-0"
                      style={{ background: BS.primary }}
                    >
                      {(user?.email?.[0] ?? "?").toUpperCase()}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block truncate leading-none">
                        {user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email || "User"}
                      </span>
                      <span className="block truncate text-[10px] mt-0.5" style={{ color: BS.muted }}>
                        {isAdmin ? "Admin" : "Sales Rep"}
                      </span>
                    </span>
                    <ChevronDown className="size-3.5 shrink-0" style={{ color: BS.muted }} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" side="top" className="w-56">
                  <DropdownMenuItem asChild>
                    <Link to="/crm/profile" className="cursor-pointer">
                      <UserCircle2 className="size-4" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="cursor-pointer">
                        <Settings className="size-4" />
                        Site Admin
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer">
                    <LogOut className="size-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </aside>

        <main className="min-w-0 flex-1 min-h-0 p-3 pb-24 sm:p-5 md:p-7 md:pb-7 overflow-y-auto overflow-x-hidden">
          <div className="mx-auto w-full max-w-[1320px]">
            {!hidePageHeader && pageMeta ? (
              <WorkspacePageTitle workspace="CRM" title={pageMeta.label} description={pageMeta.description} />
            ) : null}
            <Suspense fallback={<CrmOutletFallback />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>

      <MobileTabBar
        isAdmin={isAdmin}
        pathname={pathname}
        waitingApplications={waitingApplications}
        onMore={() => setSidebarOpen(true)}
      />

      <DevViewportIndicator />
      {forceRouteDebug ? (
        <Suspense fallback={null}>
          <CrmRouteDebugPanel navLinks={smmMenuValidations} forceOpen />
        </Suspense>
      ) : null}
    </div>
  );
}

type MobileTabItem = {
  key: string;
  label: string;
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
  to: string;
  search?: Record<string, any>;
  isActive: (p: string) => boolean;
};

function MobileTabBar({
  isAdmin,
  pathname,
  waitingApplications,
  onMore,
}: {
  isAdmin: boolean;
  pathname: string;
  waitingApplications: number;
  onMore: () => void;
}) {
  const locSearch = useRouterState({ select: (s) => s.location.search as any }) as any;
  const isMine = pathname === "/crm" && (locSearch?.mine === 1 || locSearch?.mine === "1");

  const items: MobileTabItem[] = [
    {
      key: "dashboard",
      label: "Home",
      icon: LayoutDashboard,
      to: "/crm/dashboard",
      isActive: (p) => p === "/crm/dashboard",
    },
    {
      key: "clients",
      label: "Clients",
      icon: Users,
      to: "/crm",
      isActive: (p) =>
        !isMine &&
        (p === "/crm" || (p.startsWith("/crm/") && !CLIENT_EXCLUDES.some((q) => p.startsWith(q)))),
    },
    ...(isAdmin
      ? [
          {
            key: "applications",
            label: "Apps",
            icon: Inbox,
            to: "/crm/applications",
            isActive: (p: string) => p.startsWith("/crm/applications"),
          },
          {
            key: "reports",
            label: "Reports",
            icon: FileText,
            to: "/crm/reports",
            isActive: (p: string) => p.startsWith("/crm/reports"),
          },
        ]
      : [
          {
            key: "mylist",
            label: "My List",
            icon: Star,
            to: "/crm",
            search: { mine: 1 },
            isActive: (_p: string) => isMine,
          },
          {
            key: "tasks",
            label: "Tasks",
            icon: CalendarClock,
            to: "/crm/tasks",
            isActive: (p: string) => p.startsWith("/crm/tasks"),
          },
        ]),
  ];

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-40 border-t bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden"
      style={{ borderColor: "#eceff3" }}
      aria-label="CRM Navigation"
    >
      <div className="grid grid-cols-5">
        {items.map((item) => {
          const Icon = item.icon;
          const active = item.isActive(pathname);
          const showBadge = item.key === "applications" && waitingApplications > 0;
          return (
            <Link
              key={item.key}
              to={item.to}
              search={item.search ?? ({} as any)}
              aria-current={active ? "page" : undefined}
              aria-label={item.label}
              className="relative flex flex-col items-center gap-0.5 py-2 text-[10px] font-medium transition-colors active:scale-95"
              style={{ color: active ? BS.primary : "#6c757d" }}
            >
              <Icon className="size-5" strokeWidth={1.85} />
              <span className="truncate">{item.label}</span>
              {showBadge && (
                <span className="absolute top-1 right-1/4 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-0.5 text-[9px] font-bold text-white">
                  {waitingApplications > 99 ? "99+" : waitingApplications}
                </span>
              )}
            </Link>
          );
        })}
        <button
          type="button"
          onClick={onMore}
          className="flex flex-col items-center gap-0.5 py-2 text-[10px] font-medium text-[#6c757d] transition-colors active:scale-95"
        >
          <Menu className="size-5" />
          <span>More</span>
        </button>
      </div>
    </nav>
  );
}

function CrmOutletFallback() {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Loading">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-24 rounded-xl border bg-white animate-pulse" style={{ borderColor: "#eceff3" }} />
        ))}
      </div>
      <div className="h-72 rounded-xl border bg-white animate-pulse" style={{ borderColor: "#eceff3" }} />
    </div>
  );
}

function CRMShellSkeleton() {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#f4f6f8", fontFamily: "'Poppins', system-ui, sans-serif" }}>
      <div className="h-[53px] flex-shrink-0" style={{ background: BS.dark }} />
      <div className="flex min-h-0 flex-1">
        <aside className="hidden w-[232px] shrink-0 border-r bg-[#f8f9fb] p-3 md:block" style={{ borderColor: "#eceff3" }}>
          <div className="mb-3 h-3 w-20 rounded bg-[#eceff3]" />
          <div className="space-y-1.5">
            <div className="h-8 rounded-lg bg-[#dbeafe]" />
            <div className="h-8 rounded-lg bg-[#eceff3]" />
            <div className="h-8 rounded-lg bg-[#eceff3]" />
          </div>
        </aside>
        <main className="flex-1 p-5 md:p-7">
          <CrmOutletFallback />
        </main>
      </div>
    </div>
  );
}

function CRMLoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email || !password) {
      setError("Email and password are required.");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    setSubmitting(false);
    if (error) {
      setError(error.message);
      return;
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: BS.dark, fontFamily: "'Poppins', system-ui, sans-serif" }}
    >
      <div className="w-full max-w-sm">
        <div className="text-center mb-5">
          <div className="flex justify-center text-2xl">
            <WorkspaceWordmark name="crm" />
          </div>
          <div className="text-xs uppercase tracking-wider mt-1" style={{ color: "#adb5bd" }}>
            Sales workspace sign in
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-white border rounded shadow-sm p-5 space-y-3"
          style={{ borderColor: BS.cardBorder }}
        >
          <div>
            <label className="block mb-1 text-sm font-medium" style={{ color: BS.dark }}>
              Email
            </label>
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-1.5 text-sm bg-white border rounded focus:outline-none focus:border-[#86b7fe] focus:ring-2 focus:ring-[#0d6efd]/25"
              style={{ borderColor: "#ced4da", color: BS.dark }}
              required
            />
          </div>

          <div>
            <label className="block mb-1 text-sm font-medium" style={{ color: BS.dark }}>
              Password
            </label>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-1.5 text-sm bg-white border rounded focus:outline-none focus:border-[#86b7fe] focus:ring-2 focus:ring-[#0d6efd]/25"
              style={{ borderColor: "#ced4da", color: BS.dark }}
              required
            />
          </div>

          {error && (
            <div
              className="text-sm rounded px-3 py-2"
              style={{ background: "#f8d7da", color: "#842029", border: "1px solid #f5c2c7" }}
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded text-white disabled:opacity-60"
            style={{ background: BS.primary }}
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Sign In To CRM
          </button>
        </form>
      </div>
    </div>
  );
}
